import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { BarChart3, Download, RefreshCw, Gauge, Layers, Clock, ShieldCheck, AlertTriangle, Database, Waves, TrendingUp, TrendingDown, Lightbulb, Printer, Server, Cloud, Building2, Truck, Users, Link2, ArrowUpRight, ArrowDownRight } from "lucide-react";

// Tier helpers — tolerate both "tier_1" strings and bare integers (demo seed).
const tierNum = (t) => {
  const m = String(t == null ? "" : t).match(/(\d)/);
  return m ? m[1] : null;
};
const tierLabel = (t) => {
  const n = tierNum(t);
  return n ? `Tier ${n}` : "—";
};
const isTier1 = (t) => tierNum(t) === "1";

const RATING_META = [
  { r: 4, label: "Critical (4)", color: "#dc2626" },
  { r: 3, label: "High (3)", color: "#d97706" },
  { r: 2, label: "Moderate (2)", color: "#059669" },
  { r: 1, label: "Low (1)", color: "#94a3b8" },
];

const RTO_BANDS = [
  { label: "≤1h", max: 1, color: "#dc2626" },
  { label: "≤4h", max: 4, color: "#ea580c" },
  { label: "≤24h", max: 24, color: "#d97706" },
  { label: "≤72h", max: 72, color: "#0891b2" },
  { label: ">72h", max: Infinity, color: "#059669" },
];

// Inline-SVG donut of asset criticality distribution (from the register).
const CriticalityDonut = ({ register }) => {
  const counts = RATING_META.map((m) => ({ ...m, count: register.filter((r) => r.display_rating === m.r).length }));
  const total = counts.reduce((s, c) => s + c.count, 0);
  const size = 140, stroke = 20, radius = (size - stroke) / 2, circ = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="flex items-center gap-4" data-testid="chart-criticality-donut">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        {total > 0 && counts.map((c, i) => {
          if (c.count === 0) return null;
          const frac = c.count / total;
          const dash = frac * circ;
          const seg = (
            <circle key={i} cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={c.color} strokeWidth={stroke}
              strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`} />
          );
          offset += dash;
          return seg;
        })}
        <text x={size / 2} y={size / 2 - 2} textAnchor="middle" className="fill-slate-900" style={{ fontSize: 22, fontWeight: 700 }}>{total}</text>
        <text x={size / 2} y={size / 2 + 16} textAnchor="middle" className="fill-slate-400" style={{ fontSize: 10 }}>assets</text>
      </svg>
      <div className="space-y-1">
        {counts.map((c) => (
          <div key={c.r} className="flex items-center gap-2 text-xs">
            <span className="w-3 h-3 rounded-sm" style={{ background: c.color }} />
            <span className="text-slate-600 w-24">{c.label}</span>
            <span className="font-semibold text-slate-800">{c.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Inline-SVG histogram of asset RTO bands (from the register).
const RtoHistogram = ({ register }) => {
  const buckets = RTO_BANDS.map((b, i) => {
    const lower = i === 0 ? -1 : RTO_BANDS[i - 1].max;
    const count = register.filter((r) => r.inherited_rto_hours != null && r.inherited_rto_hours > lower && r.inherited_rto_hours <= b.max).length;
    return { ...b, count };
  });
  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  const W = 260, H = 130, pad = 24, barGap = 12;
  const barW = (W - pad * 2 - barGap * (buckets.length - 1)) / buckets.length;
  return (
    <svg width="100%" height={H + 24} viewBox={`0 0 ${W} ${H + 24}`} data-testid="chart-rto-histogram">
      {buckets.map((b, i) => {
        const h = (b.count / maxCount) * (H - pad);
        const x = pad + i * (barW + barGap);
        const y = H - h;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={h} rx="3" fill={b.color} />
            <text x={x + barW / 2} y={y - 4} textAnchor="middle" style={{ fontSize: 11, fontWeight: 700 }} className="fill-slate-700">{b.count}</text>
            <text x={x + barW / 2} y={H + 14} textAnchor="middle" style={{ fontSize: 10 }} className="fill-slate-500">{b.label}</text>
          </g>
        );
      })}
    </svg>
  );
};

const PRINT_STYLES = `
@media print {
  @page { margin: 14mm; }
  body.print-report *, body.print-waves *, body.print-unit * { visibility: hidden !important; }
  body.print-report #bia-report-root, body.print-report #bia-report-root *,
  body.print-waves #recovery-waves-section, body.print-waves #recovery-waves-section *,
  body.print-unit .printing-unit, body.print-unit .printing-unit * { visibility: visible !important; }
  body.print-report #bia-report-root { position: absolute !important; left: 0; top: 0; width: 100% !important; }
  body.print-waves #recovery-waves-section { position: absolute !important; left: 0; top: 0; width: 100% !important; }
  body.print-unit .printing-unit { position: absolute !important; left: 0; top: 0; width: 100% !important; }
  .no-print { display: none !important; }
  .print-avoid-break { page-break-inside: avoid; break-inside: avoid; }
  .print-report-header { display: block !important; }
}
.print-report-header { display: none; }
`;

const ratingBadge = (r) => {
  if (r === 4) return "bg-red-100 text-red-700 border-red-300";
  if (r === 3) return "bg-amber-100 text-amber-800 border-amber-300";
  if (r === 2) return "bg-emerald-100 text-emerald-700 border-emerald-300";
  return "bg-slate-100 text-slate-600 border-slate-300";
};

const gradeColor = (g) => {
  if (g === "A") return "text-emerald-600";
  if (g === "B") return "text-lime-600";
  if (g === "C") return "text-amber-600";
  if (g === "D") return "text-orange-600";
  return "text-red-600";
};

const severityStyle = (s) => {
  if (s === "high") return "bg-red-50 border-red-300 text-red-800";
  if (s === "medium") return "bg-amber-50 border-amber-300 text-amber-800";
  return "bg-slate-50 border-slate-300 text-slate-700";
};

const Sparkline = ({ points }) => {
  if (!points || points.length < 2) return null;
  const w = 120, h = 32, pad = 3;
  const min = Math.min(...points), max = Math.max(...points);
  const range = max - min || 1;
  const step = (w - pad * 2) / (points.length - 1);
  const coords = points.map((v, i) => {
    const x = pad + i * step;
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  return (
    <svg width={w} height={h} data-testid="health-sparkline" className="overflow-visible">
      <polyline points={coords.join(" ")} fill="none" stroke="#0d9488" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={coords[coords.length - 1].split(",")[0]} cy={coords[coords.length - 1].split(",")[1]} r="2.5" fill="#0d9488" />
    </svg>
  );
};

export default function BIAReportPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [health, setHealth] = useState(null);
  const [register, setRegister] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [registerSummary, setRegisterSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [trend, setTrend] = useState(null);
  const [waves, setWaves] = useState([]);
  const [cycleBreaks, setCycleBreaks] = useState([]);
  const [insights, setInsights] = useState([]);
  const [insightCounts, setInsightCounts] = useState(null);
  const [standards, setStandards] = useState(null);
  const [outcomeGroups, setOutcomeGroups] = useState([]);
  const [outcomeSummary, setOutcomeSummary] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [k, h, r, t, w, ins, st, oc] = await Promise.all([
        api.get("/bia-engine/report/kpi-strip"),
        api.get("/bia-engine/report/program-health"),
        api.get("/bia-engine/report/asset-criticality-register"),
        api.get("/bia-engine/report/health-trend"),
        api.get("/bia-engine/report/recovery-waves"),
        api.get("/bia-engine/report/key-risk-insights"),
        api.get("/bia-engine/report/standards-alignment"),
        api.get("/bia-engine/report/outcome-register?group_by=business_unit"),
      ]);
      setKpi(k.data);
      setHealth(h.data);
      setRegister(r.data.register || []);
      setBreakdown(r.data.rpo_breakdown || []);
      setRegisterSummary(r.data.summary || null);
      setTrend(t.data);
      setWaves(w.data.waves || []);
      setCycleBreaks(w.data.cycle_breaks || []);
      setInsights(ins.data.insights || []);
      setInsightCounts(ins.data.counts || null);
      setStandards(st.data);
      setOutcomeGroups(oc.data.groups || []);
      setOutcomeSummary(oc.data.summary || null);
    } catch (e) {
      console.error("BIA report fetch failed:", e);
      toast.error("Failed to load BIA report");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const cleanup = () => {
      document.body.classList.remove("print-report", "print-waves", "print-unit");
      document.querySelectorAll(".printing-unit").forEach((e) => e.classList.remove("printing-unit"));
    };
    window.addEventListener("afterprint", cleanup);
    return () => window.removeEventListener("afterprint", cleanup);
  }, []);

  const printReport = () => { document.body.classList.add("print-report"); window.print(); };
  const printWaves = () => { document.body.classList.add("print-waves"); window.print(); };
  const printUnit = (gi) => {
    const el = document.getElementById(`outcome-unit-block-${gi}`);
    if (!el) return;
    document.querySelectorAll(".printing-unit").forEach((e) => e.classList.remove("printing-unit"));
    el.classList.add("printing-unit");
    document.body.classList.add("print-unit");
    window.print();
  };

  const exportUnit = async (unit) => {
    try {
      const res = await api.get(`/bia-engine/report/outcome-register.xlsx?unit=${encodeURIComponent(unit)}&group_by=business_unit`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `outcome_register_${unit.replace(/[^A-Za-z0-9_-]+/g, "_")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported "${unit}"`);
    } catch (e) {
      console.error("Unit export failed:", e);
      toast.error("Unit export failed");
    }
  };

  const generatedAt = useMemo(() => new Date().toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }), [register]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/bia-engine/report/asset-criticality-register.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "asset_criticality_register.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e) {
      console.error("Excel download failed:", e);
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  const tiles = kpi ? [
    { key: "assessments", label: "Assessments", value: kpi.total_assessments, sub: `${kpi.by_status?.approved || 0} approved`, icon: Layers },
    { key: "tier1", label: "Tier-1 Processes", value: kpi.tier_1_count, sub: "most critical", icon: AlertTriangle },
    { key: "avg-rto", label: "Avg RTO", value: `${kpi.avg_rto_hours}h`, sub: "recovery time", icon: Clock },
    { key: "anchored", label: "Sensitive Anchored", value: `${kpi.sensitive_assets_anchored}/${kpi.sensitive_assets_total}`, sub: "per-asset RPO", icon: ShieldCheck },
    { key: "open-gaps", label: "Open Gaps", value: kpi.open_gaps, sub: "unapproved", icon: AlertTriangle },
    { key: "health", label: "Health Score", value: kpi.health_score, sub: `Grade ${kpi.health_grade}`, icon: Gauge },
  ] : [];

  return (
    <div className="space-y-8" id="bia-report-root" data-testid="bia-report-page">
      <style>{PRINT_STYLES}</style>

      {/* Print-only boardroom report header */}
      <div className="print-report-header mb-6 border-b-2 border-slate-800 pb-4">
        <div className="text-xs uppercase tracking-widest text-slate-500">{user?.org_name || "Organization"}</div>
        <div className="text-2xl font-bold text-slate-900">Business Impact Analysis Report</div>
        <div className="text-xs text-slate-500 mt-1">Generated {generatedAt}</div>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-teal-700" /> BIA Report
          </h1>
          <p className="text-slate-500 text-sm mt-1">Program KPIs, health score and the Asset Criticality Register</p>
        </div>
        <div className="flex items-center gap-4 no-print">
          {trend && trend.history && trend.history.length >= 2 && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200" data-testid="health-trend-header">
              <Sparkline points={trend.history.map((p) => p.score)} />
              <div className="text-xs">
                {trend.baseline != null && trend.delta != null ? (
                  <div className="flex items-center gap-1 font-medium" data-testid="health-trend-delta">
                    {trend.delta >= 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> : <TrendingDown className="w-3.5 h-3.5 text-red-600" />}
                    <span className="text-slate-700">{trend.baseline.score} → {trend.current_score}</span>
                    <span className="text-slate-400">since rev {trend.baseline.revision}</span>
                  </div>
                ) : (
                  <span className="text-slate-500" data-testid="health-trend-nobaseline">Trend ({trend.history.length} pts)</span>
                )}
              </div>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={printReport} data-testid="print-report-btn">
            <Printer className="w-4 h-4 mr-1" /> Print / Save as PDF
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading} data-testid="bia-report-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="kpi-strip">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <Card key={t.key} data-testid={`kpi-tile-${t.key}`} className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{t.label}</span>
                  <Icon className="w-4 h-4 text-slate-400" />
                </div>
                <div className="text-2xl font-bold text-slate-900 mt-2" data-testid={`kpi-value-${t.key}`}>{t.value}</div>
                <div className="text-xs text-slate-400 mt-0.5">{t.sub}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Health score + penalties */}
      <Card data-testid="health-score-card" className="print-avoid-break">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Gauge className="w-5 h-5 text-teal-700" /> Program Health Score</CardTitle>
          <CardDescription>Deterministic, program-scoped scorer (sibling to the dependency health-score).</CardDescription>
        </CardHeader>
        <CardContent>
          {health && (
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex flex-col items-center justify-center px-6">
                <div className={`text-5xl font-extrabold ${gradeColor(health.grade)}`} data-testid="health-score-value">{health.score}</div>
                <div className={`text-sm font-semibold ${gradeColor(health.grade)}`}>Grade {health.grade}</div>
              </div>
              <div className="flex-1 space-y-2" data-testid="health-penalties">
                {health.penalties.map((p) => (
                  <div key={p.code} className="flex items-center justify-between text-sm border-b border-slate-100 py-1.5" data-testid={`penalty-${p.code}`}>
                    <span className="text-slate-700">{p.label} <span className="text-slate-400">({p.count})</span></span>
                    <span className={p.points > 0 ? "text-red-600 font-medium" : "text-slate-400"}>
                      {p.points > 0 ? `−${p.points} pts` : "0 pts"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Analytics mini-charts */}
      <div className="grid gap-4 print-avoid-break" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }} data-testid="report-charts">
        <Card className="print-avoid-break">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Layers className="w-5 h-5 text-teal-700" /> Asset Criticality Distribution</CardTitle>
            <CardDescription>Assets by criticality rating (from the register).</CardDescription>
          </CardHeader>
          <CardContent>
            <CriticalityDonut register={register} />
          </CardContent>
        </Card>
        <Card className="print-avoid-break">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Clock className="w-5 h-5 text-teal-700" /> RTO Band Distribution</CardTitle>
            <CardDescription>Assets bucketed by recovery time objective.</CardDescription>
          </CardHeader>
          <CardContent>
            <RtoHistogram register={register} />
          </CardContent>
        </Card>
      </div>

      {/* Key Risk Insights */}
      <Card data-testid="key-risk-insights-card" className="print-avoid-break">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Lightbulb className="w-5 h-5 text-teal-700" /> Key Risk Insights</CardTitle>
          <CardDescription>
            Deterministic rule-based findings (no AI).{insightCounts ? ` ${insightCounts.high} high · ${insightCounts.medium} medium · ${insightCounts.low} low` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {insights.length === 0 ? (
            <div className="text-sm text-slate-400 py-4 text-center" data-testid="insights-empty">No key risks detected 🎉</div>
          ) : (
            <div className="space-y-2" data-testid="insights-list">
              {insights.map((i, idx) => (
                <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg border ${severityStyle(i.severity)}`} data-testid={`insight-${i.code}-${idx}`}>
                  <Badge variant="outline" className={`uppercase text-[10px] ${severityStyle(i.severity)}`}>{i.severity}</Badge>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{i.title}</div>
                    <div className="text-xs opacity-80 mt-0.5">{i.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recovery Wave Plan */}
      <Card data-testid="recovery-waves-card" id="recovery-waves-section" className="print-avoid-break">
        <CardHeader className="pb-3 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Waves className="w-5 h-5 text-teal-700" /> Recovery Wave Plan</CardTitle>
            <CardDescription>
              Processes are ordered so none recovers before its upstream dependencies. Each wave's target window is its strictest RTO.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={printWaves} data-testid="print-waves-btn" className="no-print">
            <Printer className="w-4 h-4 mr-1" /> Print wave plan
          </Button>
        </CardHeader>
        <CardContent>
          {cycleBreaks.length > 0 && (
            <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800" data-testid="cycle-break-notice">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
              {cycleBreaks.length} dependency cycle{cycleBreaks.length > 1 ? "s" : ""} broken to order waves: {cycleBreaks.map((c) => `${c.from_name} → ${c.to_name}`).join("; ")}
            </div>
          )}
          {waves.length === 0 ? (
            <div className="text-sm text-slate-400 py-4 text-center">No assessments to sequence yet</div>
          ) : (
            <div className="space-y-3" data-testid="waves-list">
              {waves.map((w) => (
                <div key={w.wave_index} className="flex items-start gap-3 border border-slate-200 rounded-lg p-3" data-testid={`wave-${w.wave_index}`}>
                  <div className="flex flex-col items-center justify-center bg-teal-700 text-white rounded-lg px-3 py-2 min-w-[60px]">
                    <span className="text-[10px] uppercase opacity-80">Wave</span>
                    <span className="text-xl font-bold">{w.wave_index + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 mb-1">Target window: <span className="font-semibold text-slate-700">{w.target_window_hours}h</span></div>
                    <div className="flex flex-wrap gap-2">
                      {w.processes.map((p) => (
                        <Badge key={p.id} variant="outline" className="gap-1.5" data-testid={`wave-proc-${p.id}`}>
                          {p.name}
                          <span className={`text-[10px] uppercase ${p.tier === "tier_1" ? "text-red-600 font-bold" : "text-slate-400"}`}>{p.tier || "—"}</span>
                          <span className="text-[10px] text-slate-400">{p.rto}h</span>
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3" data-testid="waves-caption">Wave # is 0-based internally (shown 1-based); source and isolated processes start in the first wave. Cycles are broken at the edge entering the lowest-RTO node and shown above.</p>
        </CardContent>
      </Card>

      {/* A5 — BIA Outcome Register (grouped by organizational unit) */}
      <Card data-testid="outcome-register-card" className="print-avoid-break">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Layers className="w-5 h-5 text-teal-700" /> BIA Outcome Register</CardTitle>
          <CardDescription>
            Every in-scope assessment and its complete dependency pattern, grouped by organizational unit.
            {outcomeSummary ? ` ${outcomeSummary.total_assessments} assessments across ${outcomeSummary.units} unit${outcomeSummary.units === 1 ? "" : "s"}.` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {outcomeGroups.length === 0 ? (
            <div className="text-sm text-slate-400 py-4 text-center" data-testid="outcome-register-empty">No assessments in scope yet</div>
          ) : (
            <div className="space-y-6" data-testid="outcome-register-groups">
              {outcomeGroups.map((g, gi) => (
                <div key={g.unit} id={`outcome-unit-block-${gi}`} className="print-avoid-break" data-testid={`outcome-unit-${g.unit}`}>
                  <div className="flex items-center gap-2 mb-3 pb-1 border-b border-slate-200">
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <h3 className="text-sm font-semibold text-slate-700">{g.unit}</h3>
                    <Badge variant="secondary" className="text-[10px]">{g.assessment_count}</Badge>
                    <div className="ml-auto flex items-center gap-2 no-print">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => exportUnit(g.unit)} data-testid={`export-unit-excel-${gi}`}>
                        <Download className="w-3 h-3 mr-1" /> Export unit (Excel)
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => printUnit(gi)} data-testid={`print-unit-${gi}`}>
                        <Printer className="w-3 h-3 mr-1" /> Print unit
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}>
                    {g.assessments.map((c) => (
                      <div key={c.id} className="rounded-lg border border-slate-200 bg-white p-3 print-avoid-break" data-testid={`outcome-card-${c.id}`}>
                        {/* Header row */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {c.code && <Badge variant="outline" className="text-[10px] font-mono">{c.code}</Badge>}
                              <span className="font-semibold text-sm text-slate-900 truncate">{c.name}</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                              {c.owner ? `Owner: ${c.owner}` : "Owner: —"}{c.department_head ? ` · Head: ${c.department_head}` : ""}
                            </div>
                          </div>
                          <Badge variant="outline" className={`text-[10px] shrink-0 ${isTier1(c.tier) ? "border-red-200 text-red-700 font-bold" : "text-slate-500"}`}>{tierLabel(c.tier)}</Badge>
                        </div>
                        {/* Objectives */}
                        <div className="flex items-center gap-2 mt-2 text-[11px]">
                          <Badge variant="secondary" className="text-[10px]"><Clock className="w-3 h-3 mr-1" />RTO {c.rto_hours != null ? `${c.rto_hours}h` : "—"}</Badge>
                          <Badge variant="secondary" className="text-[10px]">RPO {c.rpo_hours != null ? `${c.rpo_hours}h` : "—"}</Badge>
                          <Badge variant="outline" className="text-[10px] capitalize">{String(c.status || "draft").replace("_", " ")}</Badge>
                        </div>

                        {!c.has_dependencies ? (
                          <div className="mt-3 text-[11px] text-slate-400 italic" data-testid={`outcome-card-empty-${c.id}`}>No dependencies mapped for this assessment.</div>
                        ) : (
                          <div className="mt-3 space-y-2 text-[11px]">
                            {c.upstream.length > 0 && (
                              <div><span className="text-slate-500 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" />Upstream</span>
                                <div className="flex flex-wrap gap-1 mt-1">{c.upstream.map((u) => (<Badge key={u.id} variant="outline" className="text-[10px] gap-1">{u.name}<span className={isTier1(u.tier) ? "text-red-600 font-bold" : "text-slate-400"}>{tierLabel(u.tier)}</span></Badge>))}</div></div>
                            )}
                            {c.downstream.length > 0 && (
                              <div><span className="text-slate-500 flex items-center gap-1"><ArrowDownRight className="w-3 h-3" />Downstream</span>
                                <div className="flex flex-wrap gap-1 mt-1">{c.downstream.map((d) => (<Badge key={d.id} variant="outline" className="text-[10px] gap-1">{d.name}<span className={isTier1(d.tier) ? "text-red-600 font-bold" : "text-slate-400"}>{tierLabel(d.tier)}</span></Badge>))}</div></div>
                            )}
                            {c.applications.length > 0 && (
                              <div><span className="text-slate-500 flex items-center gap-1"><Server className="w-3 h-3" />Applications</span>
                                <div className="flex flex-wrap gap-1 mt-1">{c.applications.map((a) => (<Badge key={a.id} variant="outline" className="text-[10px] gap-1">{a.name}<span className="text-slate-400">{tierLabel(a.inherited_tier)}</span>{a.hosting_target && <span className="text-teal-600">· {a.hosting_target}</span>}</Badge>))}</div></div>
                            )}
                            {c.data_stores.length > 0 && (
                              <div><span className="text-slate-500 flex items-center gap-1"><Database className="w-3 h-3" />Data stores</span>
                                <div className="mt-1 space-y-1">{c.data_stores.map((s, i) => (<div key={i} className="flex flex-wrap items-center gap-1" data-testid={`outcome-datastore-${c.id}-${i}`}><span className="font-medium text-slate-700">{s.asset_name}</span>{s.asset_rpo_hours != null && <Badge variant="secondary" className="text-[10px]">RPO {s.asset_rpo_hours}h</Badge>}{s.protection_hint && <span className="text-slate-500 italic">{s.protection_hint}</span>}</div>))}</div></div>
                            )}
                            {c.vendors.length > 0 && (
                              <div><span className="text-slate-500 flex items-center gap-1"><Truck className="w-3 h-3" />Vendors</span>
                                <div className="flex flex-wrap gap-1 mt-1">{c.vendors.map((v, i) => (<Badge key={i} variant="outline" className="text-[10px]">{v.name}{v.criticality ? ` · ${v.criticality}` : ""}</Badge>))}</div></div>
                            )}
                            {c.facilities.length > 0 && (
                              <div><span className="text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" />Facilities</span>
                                <div className="flex flex-wrap gap-1 mt-1">{c.facilities.map((f, i) => (<Badge key={i} variant="outline" className="text-[10px]">{f.name}{f.type ? ` · ${f.type}` : ""}</Badge>))}</div></div>
                            )}
                            {c.staffing_roles.length > 0 && (
                              <div><span className="text-slate-500 flex items-center gap-1"><Users className="w-3 h-3" />Staffing</span>
                                <div className="flex flex-wrap gap-1 mt-1">{c.staffing_roles.map((s, i) => (<Badge key={i} variant="outline" className="text-[10px] gap-1" data-testid={`outcome-staff-${c.id}-${i}`}>{s.role} {s.minimum} of {s.total}<span className={s.remote_capable ? "text-emerald-600" : "text-slate-400"}>{s.remote_capable ? "remote" : "on-site"}</span></Badge>))}</div></div>
                            )}
                            <div className="flex items-center gap-1 pt-0.5">
                              <Link2 className={`w-3 h-3 ${c.recovery_linkage.linked ? "text-emerald-600" : "text-slate-300"}`} />
                              <span className={c.recovery_linkage.linked ? "text-emerald-700" : "text-slate-400"}>
                                {c.recovery_linkage.linked ? `Linked to recovery/BCP package (${c.recovery_linkage.package_ids.length})` : "No recovery/BCP package linked"}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Asset Criticality Register */}
      <Card data-testid="asset-register-card" className="print-avoid-break">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Layers className="w-5 h-5 text-teal-700" /> Asset Criticality Register</CardTitle>
            <CardDescription>
              {registerSummary ? `${registerSummary.total_assets} assets · ${registerSummary.data_stores} data stores · ${registerSummary.shared_critical} shared-critical` : "Rating 1–4 (asset_rating + 1)"}
            </CardDescription>
          </div>
          <Button size="sm" onClick={downloadExcel} disabled={downloading} data-testid="download-register-excel" className="bg-teal-700 hover:bg-teal-800 no-print">
            <Download className="w-4 h-4 mr-1" /> Download Excel
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Department(s)</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead>RTO</TableHead>
                  <TableHead>RPO</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {register.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-6">No assets yet</TableCell></TableRow>
                ) : register.map((r) => (
                  <TableRow key={r.asset_id} data-testid={`register-row-${r.asset_id}`}>
                    <TableCell className="font-medium">{r.asset_name}</TableCell>
                    <TableCell className="capitalize text-slate-500">{r.asset_type || "—"}</TableCell>
                    <TableCell className="text-slate-500 text-xs">{(r.departments || []).join(", ") || "—"}</TableCell>
                    <TableCell><span className="text-xs uppercase text-slate-600">{r.inherited_criticality_tier || "—"}</span></TableCell>
                    <TableCell>
                      {r.display_rating != null ? (
                        <Badge variant="outline" className={ratingBadge(r.display_rating)} data-testid={`rating-badge-${r.asset_id}`}>{r.display_rating}</Badge>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell>{r.inherited_rto_hours != null ? `${r.inherited_rto_hours}h` : "—"}</TableCell>
                    <TableCell>{r.inherited_rpo_hours != null ? `${r.inherited_rpo_hours}h` : "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        {r.is_data_store && <span title="Data store (holds sensitive data)" data-testid={`flag-datastore-${r.asset_id}`}><Database className="w-4 h-4 text-sky-600" /></span>}
                        {r.shared_critical && <span title="Shared-critical (≥2 processes incl. Tier-1)" data-testid={`flag-shared-${r.asset_id}`}><AlertTriangle className="w-4 h-4 text-amber-600" /></span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Per-asset RPO breakdown */}
      <Card data-testid="rpo-breakdown-card" className="print-avoid-break">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Database className="w-5 h-5 text-teal-700" /> Per-Asset RPO Breakdown</CardTitle>
          <CardDescription>Which asset anchors which RPO, its protection strategy, and the process whose RPO it sets.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Asset</TableHead>
                  <TableHead>Per-Asset RPO</TableHead>
                  <TableHead>Protection Strategy</TableHead>
                  <TableHead>Process</TableHead>
                  <TableHead>Sets Process RPO</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {breakdown.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No sensitive assets with a per-asset RPO yet</TableCell></TableRow>
                ) : breakdown.map((b, i) => (
                  <TableRow key={i} data-testid={`rpo-breakdown-row-${i}`}>
                    <TableCell className="font-medium">{b.asset_name}</TableCell>
                    <TableCell><Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{b.asset_rpo_hours}h</Badge></TableCell>
                    <TableCell className="text-slate-600 text-xs">{b.protection_hint || "—"}</TableCell>
                    <TableCell className="text-slate-600">{b.process_name}</TableCell>
                    <TableCell>{b.sets_process_rpo ? <Badge className="bg-teal-600">Yes</Badge> : <span className="text-slate-400">—</span>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* A7 — Regulatory & Standards Alignment */}
      <Card data-testid="standards-alignment-card" className="print-avoid-break">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-teal-700" /> Regulatory &amp; Standards Alignment</CardTitle>
          <CardDescription>
            Deterministic mapping of this BIA program to applicable standards{standards?.framework_set ? ` (${standards.framework_set} framework set${standards?.industry ? `, industry: ${standards.industry}` : ""})` : ""}. Independent of the AI compliance engine.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Standard &amp; Clause</TableHead>
                  <TableHead>Requirement</TableHead>
                  <TableHead>How the BIA satisfies it</TableHead>
                  <TableHead className="text-right">Evidence</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!standards || standards.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No standards mapping available</TableCell></TableRow>
                ) : standards.rows.map((row, i) => (
                  <TableRow key={i} data-testid={`standards-row-${i}`}>
                    <TableCell className="align-top">
                      <div className="font-semibold text-slate-800">{row.standard}</div>
                      <div className="text-xs text-slate-500">{row.clause}</div>
                    </TableCell>
                    <TableCell className="align-top text-xs text-slate-600 max-w-[280px]">{row.requirement}</TableCell>
                    <TableCell className="align-top text-xs text-slate-600 max-w-[280px]">{row.how_satisfied}</TableCell>
                    <TableCell className="align-top text-right">
                      <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200" data-testid={`standards-evidence-${i}`}>{row.evidence_count}</Badge>
                      <div className="text-[10px] text-slate-400 mt-0.5 max-w-[120px] ml-auto">{row.evidence_label}</div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
